import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Bell,
  Plus,
  Edit2,
  Trash2,
  Power,
  AlertTriangle,
  Users,
  Clock,
  Shield,
  UserX,
  Eye,
  CheckCircle,
  XCircle,
  Activity
} from 'lucide-react';

// Estados de cliente disponibles
const ESTADOS_CLIENTE = [
  { value: 'ACTIVO', label: 'Activo', color: 'bg-green-500' },
  { value: 'OBSERVADO', label: 'Observado', color: 'bg-yellow-500' },
  { value: 'EN_INVESTIGACION', label: 'En Investigación', color: 'bg-orange-500' },
  { value: 'SUSPENDIDO', label: 'Suspendido', color: 'bg-red-500' },
  { value: 'BLOQUEADO', label: 'Bloqueado', color: 'bg-red-700' },
  { value: 'DADO_DE_BAJA', label: 'Dado de Baja', color: 'bg-slate-500' },
];

const SEVERIDADES = [
  { value: 'BAJA', label: 'Baja', color: 'bg-blue-500' },
  { value: 'MEDIA', label: 'Media', color: 'bg-yellow-500' },
  { value: 'ALTA', label: 'Alta', color: 'bg-orange-500' },
  { value: 'CRITICA', label: 'Crítica', color: 'bg-red-500' },
];

const AlertasCliente = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    estado: '',
    dias_umbral: 30,
    severidad: 'MEDIA'
  });

  useEffect(() => {
    loadRules();
    loadStatistics();
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      const response = await api.get('/approval-flows/v2/cliente-alert-rules');
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Error loading rules:', error);
      toast.error('Error cargando reglas de alerta');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.get('/approval-flows/v2/cliente-state-statistics');
      setStatistics(response.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleCreateRule = async () => {
    try {
      if (!formData.nombre || !formData.estado || !formData.dias_umbral) {
        toast.error('Complete todos los campos requeridos');
        return;
      }

      await api.post('/approval-flows/v2/cliente-alert-rules', formData);
      toast.success('Regla de alerta creada');
      setShowCreateDialog(false);
      resetForm();
      loadRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando regla');
    }
  };

  const handleToggleRule = async (ruleId, currentState) => {
    try {
      await api.patch(`/approval-flows/v2/cliente-alert-rules/${ruleId}/toggle`, {
        activa: !currentState
      });
      toast.success(`Regla ${!currentState ? 'activada' : 'desactivada'}`);
      loadRules();
    } catch (error) {
      toast.error('Error actualizando regla');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm('¿Eliminar esta regla de alerta?')) return;
    try {
      await api.delete(`/approval-flows/v2/cliente-alert-rules/${ruleId}`);
      toast.success('Regla eliminada');
      loadRules();
    } catch (error) {
      toast.error('Error eliminando regla');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      estado: '',
      dias_umbral: 30,
      severidad: 'MEDIA'
    });
    setSelectedRule(null);
  };

  const openEditDialog = (rule) => {
    setSelectedRule(rule);
    setFormData({
      nombre: rule.nombre,
      descripcion: rule.descripcion || '',
      estado: rule.estado,
      dias_umbral: rule.dias_umbral,
      severidad: rule.severidad
    });
    setShowEditDialog(true);
  };

  const getEstadoInfo = (estado) => {
    return ESTADOS_CLIENTE.find(e => e.value === estado) || { label: estado, color: 'bg-slate-500' };
  };

  const getSeveridadInfo = (severidad) => {
    return SEVERIDADES.find(s => s.value === severidad) || { label: severidad, color: 'bg-slate-500' };
  };

  return (
    <div className="p-6 space-y-6" data-testid="alertas-cliente-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bell className="text-amber-600" />
            Alertas Automáticas de Clientes
          </h1>
          <p className="text-slate-600 mt-1">
            Configura alertas automáticas basadas en el estado de los clientes
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-rule-btn">
          <Plus size={16} className="mr-2" />
          Nueva Regla
        </Button>
      </div>

      {/* Estadísticas de Estados */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {ESTADOS_CLIENTE.map(estado => (
            <Card key={estado.value} className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-3 h-3 rounded-full ${estado.color}`} />
                <span className="text-sm font-medium text-slate-700">{estado.label}</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {statistics.por_estado?.[estado.value] || 0}
              </p>
            </Card>
          ))}
        </div>
      )}

      {/* Clientes en Estados Críticos */}
      {statistics?.clientes_criticos?.length > 0 && (
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="text-red-500" />
            Clientes en Estados Críticos (más de 7 días)
          </h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>CUIT</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Días en Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statistics.clientes_criticos.slice(0, 10).map((cliente, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{cliente.razon_social || '-'}</TableCell>
                    <TableCell>{cliente.cuit_limpio}</TableCell>
                    <TableCell>
                      <Badge className={`${getEstadoInfo(cliente.estado_cliente).color} text-white`}>
                        {getEstadoInfo(cliente.estado_cliente).label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-red-600 font-medium">{cliente.dias_en_estado} días</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Lista de Reglas */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Shield className="text-blue-600" />
          Reglas de Alerta Configuradas
        </h3>

        {loading ? (
          <div className="text-center py-8 text-slate-500">Cargando reglas...</div>
        ) : rules.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Bell size={48} className="mx-auto mb-4 opacity-30" />
            <p>No hay reglas de alerta configuradas</p>
            <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
              <Plus size={16} className="mr-2" />
              Crear Primera Regla
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Estado Monitoreado</TableHead>
                  <TableHead>Umbral (días)</TableHead>
                  <TableHead>Severidad</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map(rule => (
                  <TableRow key={rule.id} data-testid={`rule-row-${rule.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{rule.nombre}</p>
                        {rule.descripcion && (
                          <p className="text-xs text-slate-500">{rule.descripcion}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getEstadoInfo(rule.estado).color} text-white`}>
                        {getEstadoInfo(rule.estado).label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock size={14} className="text-slate-400" />
                        <span>{rule.dias_umbral} días</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={`${getSeveridadInfo(rule.severidad).color} text-white`}
                      >
                        {getSeveridadInfo(rule.severidad).label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {rule.activa ? (
                        <Badge className="bg-green-100 text-green-700">Activa</Badge>
                      ) : (
                        <Badge variant="secondary">Inactiva</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleRule(rule.id, rule.activa)}
                          title={rule.activa ? 'Desactivar' : 'Activar'}
                        >
                          {rule.activa ? <Power size={14} className="text-green-600" /> : <Power size={14} className="text-slate-400" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(rule)}
                        >
                          <Edit2 size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteRule(rule.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Información del Sistema */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Activity className="text-blue-600 mt-1" />
          <div>
            <h4 className="font-semibold text-blue-900">Ejecución Automática</h4>
            <p className="text-sm text-blue-700 mt-1">
              Las reglas de alerta se verifican automáticamente todos los días a las 6:00 AM. 
              Cuando un cliente permanece en un estado por más tiempo del umbral configurado, 
              se genera una alerta automáticamente y se notifica al equipo.
            </p>
          </div>
        </div>
      </Card>

      {/* Dialog Crear Regla */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="text-amber-600" />
              Nueva Regla de Alerta
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre de la Regla *</Label>
              <Input
                placeholder="Ej: Cliente en investigación prolongada"
                value={formData.nombre}
                onChange={e => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
              />
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                placeholder="Descripción de cuándo debe dispararse esta alerta..."
                value={formData.descripcion}
                onChange={e => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estado a Monitorear *</Label>
                <Select
                  value={formData.estado}
                  onValueChange={v => setFormData(prev => ({ ...prev, estado: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADOS_CLIENTE.map(estado => (
                      <SelectItem key={estado.value} value={estado.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${estado.color}`} />
                          {estado.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Umbral (días) *</Label>
                <Input
                  type="number"
                  min={1}
                  value={formData.dias_umbral}
                  onChange={e => setFormData(prev => ({ ...prev, dias_umbral: parseInt(e.target.value) || 1 }))}
                />
              </div>
            </div>

            <div>
              <Label>Severidad de la Alerta</Label>
              <Select
                value={formData.severidad}
                onValueChange={v => setFormData(prev => ({ ...prev, severidad: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERIDADES.map(sev => (
                    <SelectItem key={sev.value} value={sev.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${sev.color}`} />
                        {sev.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <p className="text-slate-700">
                <strong>Ejemplo:</strong> Si configuras "En Investigación" con umbral de 30 días, 
                se generará una alerta automática para cada cliente que lleve más de 30 días 
                en estado "En Investigación".
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateRule}
              disabled={!formData.nombre || !formData.estado || !formData.dias_umbral}
            >
              Crear Regla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar Regla (similar al crear) */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="text-blue-600" />
              Editar Regla de Alerta
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre de la Regla *</Label>
              <Input
                value={formData.nombre}
                onChange={e => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                disabled
              />
              <p className="text-xs text-slate-500 mt-1">El nombre no se puede modificar</p>
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={e => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estado a Monitorear</Label>
                <Select
                  value={formData.estado}
                  onValueChange={v => setFormData(prev => ({ ...prev, estado: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADOS_CLIENTE.map(estado => (
                      <SelectItem key={estado.value} value={estado.value}>
                        {estado.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Umbral (días)</Label>
                <Input
                  type="number"
                  min={1}
                  value={formData.dias_umbral}
                  onChange={e => setFormData(prev => ({ ...prev, dias_umbral: parseInt(e.target.value) || 1 }))}
                />
              </div>
            </div>

            <div>
              <Label>Severidad</Label>
              <Select
                value={formData.severidad}
                onValueChange={v => setFormData(prev => ({ ...prev, severidad: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERIDADES.map(sev => (
                    <SelectItem key={sev.value} value={sev.value}>
                      {sev.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button onClick={() => {
              // TODO: Implementar update
              toast.info('Actualización pendiente - los cambios no se guardan');
              setShowEditDialog(false);
            }}>
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AlertasCliente;
